import { queryClient } from '@frontend/api';

import { getPartThumbnailImageQueryKey } from './getPartThumbnailImageQueryKey.util';

/**
 * Part files whose thumbnail the next fetch must take from the server, not the
 * browser's HTTP cache.
 */
const reloadOnNextFetch = new Set<string>();

/**
 * Take a part file's reload mark, if it has one.
 *
 * @param partFileUuid uuid of the part file about to be fetched.
 * @returns whether this fetch has to skip the browser's HTTP cache.
 */
export const takePartThumbnailReload = (partFileUuid: string): boolean =>
  reloadOnNextFetch.delete(partFileUuid);

/**
 * Refetch a part file's thumbnail after the server replaced it.
 *
 * The thumbnail query never goes stale on its own, so it only changes when told to.
 * The image is also served from a URL that never changes, with a max-age of an
 * hour, so a plain refetch is answered from the browser's HTTP cache and shows the
 * old image. This one reloads instead, which also replaces the cached copy.
 *
 * A thumbnail nothing has fetched is left alone: it loads the new one when it is
 * first shown.
 *
 * @param partFileUuid uuid of the part file whose thumbnail changed.
 */
export const refreshPartThumbnailImage = (partFileUuid: string) => {
  const queryKey = getPartThumbnailImageQueryKey({ partFileUuid });
  if (queryClient.getQueryState(queryKey)) {
    reloadOnNextFetch.add(partFileUuid);
    queryClient.invalidateQueries({ queryKey, exact: true });
  }
};
